export const PageHeader = ({ overline, title, description, action, testid }) => (
  <div
    data-testid={testid}
    className="mb-5 flex flex-col gap-4 border-b border-border pb-4 sm:mb-6 sm:pb-5 sm:flex-row sm:items-end sm:justify-between"
  >
    <div>
      {overline && <div className="overline mb-2 text-[#002FA7]">{overline}</div>}
      <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F172A] sm:text-3xl">
        {title}
      </h1>
      {description && <p className="mt-1.5 max-w-2xl text-sm text-slate-500">{description}</p>}
    </div>
    {action}
  </div>
);

export const StatCard = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "text-[#0F172A]",
    danger: "text-[#FF3B30]",
    success: "text-[#10B981]",
    primary: "text-[#002FA7]",
  };
  return (
    <div
      data-testid={testid}
      className="group h-full border border-border bg-white p-3 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 sm:p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="overline text-[0.6rem] text-slate-500 sm:text-[0.7rem]">{label}</div>
        {Icon && <Icon size={16} className="shrink-0 text-slate-400 sm:hidden" />}
        {Icon && <Icon size={18} className="hidden shrink-0 text-slate-400 sm:block" />}
      </div>
      <div className={`font-display mt-2 text-2xl font-bold tracking-tight sm:mt-3 sm:text-3xl ${tones[tone]}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[0.7rem] leading-snug text-slate-500 sm:text-xs">{sub}</div>}
    </div>
  );
};

const STATUS_STYLES = {
  active: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  open: "bg-[#FFF7DB] text-[#8A6A00] border-[#F2DE9B]",
  awarded: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  sold: "bg-slate-100 text-slate-600 border-slate-300",
  withdrawn: "bg-slate-100 text-slate-500 border-slate-200",
  submitted: "bg-[#EEF2FF] text-[#002FA7] border-[#C7D2FE]",
  pending: "bg-[#FFF7DB] text-[#8A6A00] border-[#F2DE9B]",
  open: "bg-[#FFF7DB] text-[#8A6A00] border-[#F2DE9B]",
  proposed: "bg-slate-100 text-slate-600 border-slate-300",
  in_review: "bg-[#EEF2FF] text-[#002FA7] border-[#C7D2FE]",
  reviewed: "bg-[#EEF2FF] text-[#002FA7] border-[#C7D2FE]",
  approved: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  ordered: "bg-[#EEF2FF] text-[#002FA7] border-[#C7D2FE]",
  delivered: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  implemented: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  rejected: "bg-[#FFECEB] text-[#B3241B] border-[#F6B9B5]",
  closed: "bg-slate-100 text-slate-600 border-slate-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
  urgent: "bg-[#FFECEB] text-[#B3241B] border-[#F6B9B5]",
  high: "bg-[#FFF1E6] text-[#9A4B00] border-[#F5CFAE]",
  normal: "bg-slate-100 text-slate-600 border-slate-300",
  low: "bg-slate-100 text-slate-500 border-slate-200",
};

export const StatusBadge = ({ value, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wider ${
      STATUS_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-300"
    }`}
  >
    {String(value || "").replace("_", " ")}
  </span>
);

export const MobileRow = ({ title, badges, fields, actions, media, testid }) => (
  <div data-testid={testid} className="border-b border-border p-4 last:border-0">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-display text-base font-semibold leading-tight tracking-tight text-[#0F172A]">
          {title}
        </div>
        {badges && <div className="mt-2 flex flex-wrap items-center gap-1.5">{badges}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
    {fields && (
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        {fields.map(({ label, value, tone }) => (
          <div key={label} className="min-w-0">
            <dt className="overline text-[0.6rem] text-slate-400">{label}</dt>
            <dd className={`break-words text-sm ${tone === "danger" ? "font-bold text-[#FF3B30]" : "text-[#0F172A]"}`}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
    )}
    {media}
  </div>
);

export const EmptyState = ({ title, description, testid }) => (
  <div data-testid={testid} className="border border-dashed border-border bg-white p-10 text-center">
    <div className="font-display text-base font-semibold text-[#0F172A]">{title}</div>
    {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
  </div>
);
